import { BROWSER_ONLY_SOLID_PASSAGE_STATUSES } from '../ability/browser-only-layers.js';
import { floorBandShape, withExteriorOpenGround } from '../inventory/lift-occupancy.js';
import { insideUnion } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { occupancyOverlap } from '../../../../ft3d/engine/ft3d-move-core.js';
import { movementFootprint, type MovementBody } from './body-geometry.js';
import type { CrossingHardObstacle } from './crossing-route.js';
import type { RouteWallWorld } from './route-walls.js';

type Row = Record<string, unknown>;
type StatusReader<T> = { hasStatus: (row: T, key: string) => boolean };

/** Scene projections already carry the canonical status keys; do not infer them from labels. */
export const projectedMovementStatusReader = {
  hasStatus: (row: { reviewMetadata?: { statusEffects?: readonly { key: string }[] } }, key: string): boolean =>
    (row.reviewMetadata?.statusEffects ?? []).some(status => status.key === key),
};

/** Use the caller's existing canonical-status reader in both scene and held-state movement. */
export function moverPassesSolidObjects<T = Row>(read: StatusReader<T>, mover: T): boolean {
  return BROWSER_ONLY_SOLID_PASSAGE_STATUSES.some(key => read.hasStatus(mover, key));
}

export const INCORPOREAL_END_INSIDE_MESSAGE = 'Incorporeal movement refused: ending inside a solid object requires 1d10 damage, which is not yet supported. Choose a clear landing.';

/** Phasing changes horizontal collision only. The existing floor selector still owns every foot. */
export function solidPassageFloorSupported(world: RouteWallWorld, route: ReadonlyArray<readonly number[]>, z: number): boolean {
  if (!world.shape) return Math.abs(z) <= 1e-6;
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1]!, b = route[i]!;
    const steps = Math.max(1, Math.ceil(Math.hypot(b[0]! - a[0]!, b[1]! - a[1]!)));
    for (let step = 0; step <= steps; step++) {
      const x = a[0]! + (b[0]! - a[0]!) * step / steps, y = a[1]! + (b[1]! - a[1]!) * step / steps;
      const band = floorBandShape(world.shape, x, y, z, world.canvasExterior);
      if (!band || Math.abs(band.floor - z) > .01
        || !insideUnion(withExteriorOpenGround(band.shape, x, y, x, y, 0, world.canvasExterior), x, y)) return false;
    }
  }
  return true;
}

/** Endpoint only: transit through a prop is free; resting inside it still owes the unported damage. */
export function solidObjectAt(body: MovementBody, at: { x: number; y: number; z: number }, props: readonly CrossingHardObstacle[]): boolean | null {
  const footprint = movementFootprint(body, at.x, at.y, at.z);
  let unknown = false;
  for (const prop of props) {
    if (!prop.physical) { unknown = true; continue; }
    if (occupancyOverlap(footprint, prop.physical)) return true;
  }
  return unknown ? null : false;
}
